/**
 * "Termos e privacidade" on the person record (D580): which version of each legal document the person accepted and when, and the
 * privacy requests (data export, erasure) with their status — what an agent needs to answer "did they accept?" and "where is my
 * request?".
 */
import { type ReactElement } from 'react';
import { type DirectoryUser } from '@vello/api-client';
import { Badge, Ink, RecordRow, RecordSection, Text } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { PersonCard, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';

const requestTone = (status: string): 'success' | 'warning' | 'neutral' => (status === 'COMPLETED' ? 'success' : status === 'REQUESTED' || status === 'PROCESSING' ? 'warning' : 'neutral');

export function PersonLegalCard({ user }: { user: DirectoryUser }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();
  const { state, reload } = useCardRead(() => runtime.personInsights.legal(user.id), [runtime, user.id]);

  return (
    <PersonCard
      title={t('prec.legal.title')}
      what={t('prec.what.legal')}
      state={state}
      reload={reload}
      isEmpty={(r) => r.acceptances.length === 0 && r.privacyRequests.length === 0}
      empty={t('prec.legal.empty')}
    >
      {(r) => (
        <>
          <RecordSection>
            <Text as="h3" role="label">
              {t('prec.legal.acceptances')}
            </Text>
            {r.acceptances.length === 0 ? (
              <Text as="p" role="caption" tone="secondary">
                {t('prec.legal.acceptances.empty')}
              </Text>
            ) : (
              r.acceptances.map((a) => (
                <RecordRow key={`${a.documentCode}-${a.version}`} dense>
                  <Ink strong>{v('legal.document_kind', a.documentCode)}</Ink>
                  <Text role="caption" tone="secondary" mono>
                    {t('prec.legal.version', { version: a.version })}
                  </Text>
                  <span className="vello-grow" />
                  <Ink mono>{fmt.when(a.acceptedAt)}</Ink>
                </RecordRow>
              ))
            )}
          </RecordSection>
          <RecordSection>
            <Text as="h3" role="label">
              {t('prec.legal.requests')}
            </Text>
            {r.privacyRequests.length === 0 ? (
              <Text as="p" role="caption" tone="secondary">
                {t('prec.legal.requests.empty')}
              </Text>
            ) : (
              r.privacyRequests.map((q) => (
                <RecordRow key={q.requestId} dense>
                  <Ink strong>{v('privacy.data_rights_kind', q.kind)}</Ink>
                  <Badge tone={requestTone(q.status)}>{v('privacy.data_rights_status', q.status)}</Badge>
                  <span className="vello-grow" />
                  <Ink mono>{fmt.when(q.createdAt)}</Ink>
                </RecordRow>
              ))
            )}
          </RecordSection>
        </>
      )}
    </PersonCard>
  );
}
